import type { DocFieldSchema } from "@/lib/erp/meta";
import type { ChildTableRow, RowValidationError } from "../types";

/**
 * Fieldnames that are system/meta fields on Frappe child table rows and should never be
 * used for business-field validation/presence detection.
 */
export const CHILD_TABLE_META_FIELDNAMES = new Set<string>([
  "idx",
  "docstatus",
  "__islocal",
  "__unsaved",
  "name",
  "doctype",
  "owner",
  "creation",
  "modified",
  "modified_by",
  "parent",
  "parenttype",
  "parentfield",
]);

/**
 * Check if a field value is empty
 */
function isEmptyValue(value: any, fieldtype: string): boolean {
  if (value === null || value === undefined || value === "") {
    return true;
  }
  
  // For Check fields, 0/false is valid (not empty)
  if (fieldtype === "Check") {
    return false;
  }
  
  // For arrays, check if empty
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  
  return false;
}

/**
 * Check if a child-table row is effectively empty (placeholder row).
 * 
 * We skip required-field validation for rows that have no user-entered values.
 * This prevents false validation failures when UIs render pre-created empty rows
 * (e.g., image/gallery tables with 5 blank rows).
 */
export function isEffectivelyEmptyRow(row: ChildTableRow, fields: DocFieldSchema[]): boolean {
  // Deleted rows are irrelevant
  if (row.__deleted) {
    return true;
  }

  const isLocal = !!row.__islocal;

  // Consider a row "non-empty" if ANY field has a meaningful user-entered value.
  //
  // IMPORTANT: Many child tables (e.g., item images) create placeholder rows that
  // come with defaults like:
  // - Check fields default to 0
  // - Select fields default to first option (e.g., "image")
  // - Numeric fields default to 0
  //
  // We must NOT treat these defaults as "user-entered", otherwise required-field
  // validation fires on placeholder rows and blocks saving items with no media.
  for (const field of fields) {
    const value = row[field.fieldname];

    if (isEmptyValue(value, field.fieldtype)) {
      continue;
    }

    // IMPORTANT: Placeholder heuristics are ONLY safe for local, client-created rows.
    // For server rows, any non-empty value must make the row "present" so we don't
    // accidentally skip validation of real persisted data.
    if (!isLocal) {
      return false;
    }

    // For Check fields, only treat checked/true as meaningful for "row presence".
    // (Unchecked false/0 is usually just a default placeholder value.)
    if (field.fieldtype === "Check") {
      if (value === 1 || value === true) {
        return false;
      }
      continue;
    }

    // For numeric fields, treat 0 as non-meaningful for presence detection.
    // (0 is commonly a placeholder/default in child rows.)
    if (
      (field.fieldtype === "Int" ||
        field.fieldtype === "Float" ||
        field.fieldtype === "Currency" ||
        field.fieldtype === "Percent") &&
      (value === 0 || value === "0")
    ) {
      continue;
    }

    // If schema defines a default and the current value equals it, ignore it for presence.
    // This prevents defaults like media_type="image" from making a row look "filled".
    if (field.default !== undefined && value === field.default) {
      continue;
    }

    // Select fields often get initialized to the first option even when the schema
    // doesn't declare an explicit default. Treat first option as placeholder.
    if (field.fieldtype === "Select" && typeof value === "string" && typeof field.options === "string") {
      const firstOption = field.options
        .split("\n")
        .map((s) => s.trim())
        .filter((s) => s.length > 0)[0];
      if (firstOption && value.trim() === firstOption) {
        continue;
      }
    }

    // Dev/placeholder auto-fill values should not make a row "present".
    // We only ignore these for local rows to avoid masking real server data.
    if (
      typeof value === "string" &&
      value.startsWith("AUTO-") &&
      value.includes(`${field.fieldname}-`)
    ) {
      continue;
    }

    // Any other non-empty, non-default value means row is not empty.
    return false;
  }

  return true;
}

/**
 * Validate a single row against required fields
 */
export function validateRow(
  row: ChildTableRow,
  rowIndex: number,
  fields: DocFieldSchema[],
  serverFetchTriggerFields?: string[]
): RowValidationError[] {
  const errors: RowValidationError[] = [];
  
  // Skip validation for deleted rows
  if (row.__deleted) {
    return errors;
  }

  // Skip placeholder rows (no user-entered values)
  const empty = isEffectivelyEmptyRow(row, fields);
  if (empty) {
    return errors;
  }
  
  fields.forEach((field) => {
    if (field.reqd === 1) {
      const value = row[field.fieldname];

      // Skip client-side required validation for fields the server auto-populates.
      // read_only fields can't be filled by the user — they're set by fetch_from,
      // calculated formulas, or controller logic (e.g. Stock Entry's validate_item
      // auto-fills stock_uom, uom, conversion_factor from the Item master).
      // The server validates these after auto-population, so client-side checks
      // just block legitimate saves.
      if (field.read_only === 1 || field.fetch_from) {
        return;
      }

      // Skip validation for fields that will be auto-populated by server fetch.
      // When a trigger field (e.g. item_code) is filled, the server method returns
      // dependent fields (uom, conversion_factor, etc.). Client validation should
      // not block the save while the fetch is in-flight or if it already populated.
      if (serverFetchTriggerFields?.length) {
        const triggerFilled = serverFetchTriggerFields.some((tf) => {
          const v = row[tf];
          return v !== null && v !== undefined && v !== "";
        });
        if (triggerFilled && !serverFetchTriggerFields.includes(field.fieldname)) {
          return;
        }
      }

      // Required Check fields must be checked (align with typical Frappe semantics).
      if (field.fieldtype === "Check") {
        const checked = value === 1 || value === true;
        if (!checked) {
          errors.push({
            rowIndex,
            fieldname: field.fieldname,
            fieldLabel: field.label || field.fieldname,
            message: `${field.label || field.fieldname} must be checked`,
          });
        }
        return;
      }

      if (isEmptyValue(value, field.fieldtype)) {
        const label = field.label || field.fieldname;
        errors.push({
          rowIndex,
          fieldname: field.fieldname,
          fieldLabel: label,
          message: `${label} is required`,
        });
      }
    }
  });
  
  return errors;
}

/**
 * Validate all rows in a child table
 */
export function validateChildTable(
  rows: ChildTableRow[],
  fields: DocFieldSchema[],
  _tableLabel: string,
  serverFetchTriggerFields?: string[]
): RowValidationError[] {
  const allErrors: RowValidationError[] = [];

  rows.forEach((row, index) => {
    if (row.__deleted) {
      return;
    }

    const rowErrors = validateRow(row, index, fields, serverFetchTriggerFields);
    allErrors.push(...rowErrors);
  });
  
  return allErrors;
}

/**
 * Format validation errors for display
 */
export function formatValidationErrors(
  errors: RowValidationError[],
  _tableLabel: string
): string {
  if (errors.length === 0) {
    return "";
  }
  
  const errorMessages = errors.map(
    (err) => `Row ${err.rowIndex + 1}: ${err.message}`
  );

  // Keep this UI-friendly and let callers provide the table label context.
  // (Avoid duplicating "Row X" prefixes in lower layers.)
  return errorMessages.join("\n");
}

